import React from 'react';
import { Group } from '@visx/group';
import { Pie as PieShape } from '@visx/shape';
import { PieArcDatum } from 'd3';
import { z } from 'zod/v4';
import {
  ChartAccessorFunctions,
  ChartDimensions,
  ChartFormatters,
  DatumId,
  WithColorAccessorFunctions,
  WithDataLabels,
  WithMargin,
  WithPieVariant,
} from '../../core/Chart.types';
import { useChartInteractionDispatch, useChartInteractionState } from '../../core/ChartInteractionProvider';
import { formatPercentage } from '../../utils/formatters';
import { typedMemo } from '../../utils/typedMemo';
import { DonutCenter } from './DonutCenter';
import { computeSizeAndPosition } from './pie.utils';
import { PieSlice } from './PieSlice';
import { adjustArcsForHighlight, adjustArcsForSelection, computeArcPath, DONUT_INNER_RADIUS_RATIO } from './PieSlice.utils';

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };
const MemoizedPieSlice = typedMemo(PieSlice);

export type DonutCenterContentProps<Datum> = {
  centerValue?: string | number;
  centerLabel?: string;
  renderCenter?: (props: { innerRadius: number; data: Datum[]; highlightedItem: DatumId | null }) => React.ReactNode;
};

export type PieProps<Datum> = {
  data: Datum[];
  total?: number;
} & ChartAccessorFunctions<NoInfer<Datum>> &
  WithDataLabels &
  ChartDimensions &
  WithColorAccessorFunctions<NoInfer<Datum>> &
  WithPieVariant &
  DonutCenterContentProps<NoInfer<Datum>> &
  Partial<WithMargin>;

export function Pie<Datum>({
  getValue,
  getColor,
  getSelected,
  getIdentifier,
  valueFormatter,
  labelFormatter,
  width,
  height,
  data,
  dataLabelMode,
  variant = 'pie',
  centerValue,
  centerLabel,
  renderCenter,
  total,
  margin = defaultMargin,
}: PieProps<Datum> & ChartFormatters<Datum>) {
  const { setHighlight, handleItemClick, showTooltip, hideTooltip } = useChartInteractionDispatch<Datum>();
  const { highlightedItem, isInteractive, isPinned } = useChartInteractionState();
  const { centerX, centerY, radius, effectiveDataLabelMode, polylineScale } = React.useMemo(
    () => computeSizeAndPosition(width, height, margin.top, margin.bottom, margin.left, margin.right, dataLabelMode),
    [width, height, margin.top, margin.bottom, margin.left, margin.right, dataLabelMode]
  );

  const isDonut = variant === 'donut';
  const innerRadius = isDonut ? radius * DONUT_INNER_RADIUS_RATIO : 0;

  const getDataLabel = (arc: PieArcDatum<Datum>) => valueFormatter?.(arc.data, { where: 'data-label' }) ?? getValue(arc.data);
  const getArcColor = (arc: PieArcDatum<Datum>) => getColor(arc.data);

  const extractItemDescriptor = React.useCallback(
    (e: React.MouseEvent<SVGGElement, MouseEvent>) => {
      const datumId = extractDatumIdFromEvent(e);
      const index = extractIndexFromEvent(e);
      const datum = data.find(d => getIdentifier(d) === datumId);
      if (!datum) throw new Error('Expected to find datum for id.');
      return { id: datumId, datum, index };
    },
    [data, getIdentifier]
  );

  const handleLeave = React.useCallback(() => {
    setHighlight(null);
    hideTooltip();
  }, [setHighlight, hideTooltip]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<SVGGElement, MouseEvent>) => handleItemClick(e, extractItemDescriptor(e)),
    [extractItemDescriptor, handleItemClick]
  );

  const handleEnter = React.useCallback(
    (e: React.PointerEvent<SVGPathElement>) => {
      const item = extractItemDescriptor(e);
      setHighlight(item);
      if (!isPinned) showTooltip({ data: item.datum, x: e.clientX, y: e.clientY });
    },
    [extractItemDescriptor, setHighlight, isPinned, showTooltip]
  );

  const handleHover = React.useCallback(
    (e: React.PointerEvent<SVGPathElement>) => (e.type === 'pointerleave' ? handleLeave() : handleEnter(e)),
    [handleEnter, handleLeave]
  );

  return (
    <Group top={centerY + margin.top} left={centerX + margin.left}>
      <PieShape data={data} pieValue={getValue} outerRadius={radius} innerRadius={innerRadius} cornerRadius={0}>
        {({ arcs, path }) => {
          const highlightedIndex = highlightedItem !== null ? arcs.findIndex(a => getIdentifier(a.data) === highlightedItem) : null;
          const selectedIndices = new Set(arcs.flatMap((a, i) => (getSelected?.(a.data) ? [i] : [])));

          const adjustedAngles = adjustArcsForHighlight(adjustArcsForSelection(arcs, selectedIndices), highlightedIndex);

          return (
            <g role="list" aria-label="Pie chart data" data-has-selection={selectedIndices.size > 0 || undefined}>
              {arcs.map((arc, i) => {
                const { index, value, data, endAngle, startAngle, padAngle } = arc;
                const adjusted = adjustedAngles[i];
                const id = getIdentifier(arc.data);
                const highlighted = highlightedItem === id;
                const selected = !!getSelected?.(arc.data);
                const color = getArcColor(arc);
                const label = getDataLabel(arc);
                const baseRadius = path.outerRadius()({ index, value, data, endAngle, startAngle, padAngle }) || 0;
                const arcPath = computeArcPath(adjusted, baseRadius, selected, innerRadius);
                if (!arcPath) throw new Error('Expected arcPath to be defined');

                const percentageRatio = total ? getValue(arc.data) / total : (arc.endAngle - arc.startAngle) / (2 * Math.PI);
                const sliceAriaLabel = `${labelFormatter?.(arc.data, { where: 'legend' })}, ${formatPercentage.format(percentageRatio)}`;

                return (
                  <MemoizedPieSlice
                    key={id}
                    datumId={id}
                    arcPath={arcPath}
                    baseRadius={baseRadius}
                    color={color}
                    label={label}
                    highlighted={highlighted}
                    selected={selected}
                    isInteractive={isInteractive}
                    dataLabelMode={effectiveDataLabelMode}
                    polylineScale={polylineScale}
                    total={total}
                    datum={arc.data}
                    onPointerEnter={handleHover}
                    onPointerLeave={handleHover}
                    onClick={handleClick}
                    data={arc.data}
                    index={arc.index}
                    padAngle={arc.padAngle}
                    value={arc.value}
                    startAngle={adjusted.startAngle}
                    endAngle={adjusted.endAngle}
                    aria-label={sliceAriaLabel}
                  />
                );
              })}
            </g>
          );
        }}
      </PieShape>
      {isDonut &&
        (renderCenter ? (
          renderCenter({ innerRadius, data, highlightedItem })
        ) : (
          <DonutCenter innerRadius={innerRadius} data={data} getValue={getValue} centerValue={centerValue} centerLabel={centerLabel} />
        ))}
    </Group>
  );
}

const extractDatumIdFromEvent = (e: React.MouseEvent<SVGGElement, MouseEvent>) =>
  z.string().nonempty().parse(e.currentTarget.dataset.datumId);
const extractIndexFromEvent = (e: React.MouseEvent<SVGGElement, MouseEvent>) =>
  z.coerce.number().int().parse(e.currentTarget.dataset.index);
